import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { API_URL } from '../../services/api';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import { CheckCircle, ClipboardList, Lightbulb } from 'lucide-react';
import '../AllCss/SatisfactionSurvey.css';

const TYPE_MAP = {
  'post-purchase': 'post_purchase',
  service: 'service_satisfaction',
  nps: 'nps_pulse',
  ideas: 'product_ideas',
};

const FOLLOW_UP = [
  { value: '', label: 'Optional: what should we improve?' },
  { value: 'sales', label: 'Sales / buying process' },
  { value: 'service', label: 'Service / visit experience' },
  { value: 'website', label: 'Website / online tools' },
  { value: 'pricing', label: 'Pricing / transparency' },
  { value: 'other', label: 'Other' },
];

const IDEA_MIN_CHARS = 20;

function SatisfactionSurveyPage() {
  const { surveyType: pathType } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((s) => s.auth);

  const surveyType = TYPE_MAP[pathType] || null;
  const appointmentIdFromQuery = searchParams.get('appointmentId') || '';
  const carIdFromQuery = searchParams.get('carId') || '';

  const [npsScore, setNpsScore] = useState(null);
  const [overallRating, setOverallRating] = useState(null);
  const [processRating, setProcessRating] = useState(null);
  const [openFeedback, setOpenFeedback] = useState('');
  const [followUpCategory, setFollowUpCategory] = useState('');
  const [ideaFields, setIdeaFields] = useState({
    featureIdeas: '',
    uiImprovements: '',
    integrationsPlugins: '',
    anythingElse: '',
  });
  const [respondentName, setRespondentName] = useState(user?.fullName || '');
  const [respondentEmail, setRespondentEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user) {
      setRespondentName((n) => n || user.fullName || '');
      setRespondentEmail((e) => e || user.email || '');
    }
  }, [user]);

  const title = useMemo(() => {
    if (surveyType === 'post_purchase') return 'Post-purchase feedback';
    if (surveyType === 'service_satisfaction') return 'How was your visit?';
    if (surveyType === 'nps_pulse') return 'Quick recommendation check-in';
    if (surveyType === 'product_ideas') return 'Ideas for features & improvements';
    return 'Customer survey';
  }, [surveyType]);

  const ideaTotalLength = useMemo(() => {
    return Object.values(ideaFields)
      .map((s) => s.trim())
      .join('')
      .length;
  }, [ideaFields]);

  if (!surveyType) {
    return (
      <div className="survey-page">
        <Navbar />
        <main className="survey-main">
          <p>Unknown survey type.</p>
          <Link to="/">Home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (surveyType === 'service_satisfaction' && !isAuthenticated) {
    return (
      <div className="survey-page">
        <Navbar />
        <main className="survey-main survey-card">
          <h1>{title}</h1>
          <p>Please sign in to rate a completed appointment.</p>
          <Link className="survey-btn survey-btn--primary" to={`/auth?redirect=${encodeURIComponent(`/survey/service?appointmentId=${appointmentIdFromQuery}`)}`}>
            Sign in
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const submitClassic = async (e) => {
    e.preventDefault();
    setError('');
    if (npsScore === null || npsScore === '') {
      setError('Please select a score from 0 (not likely) to 10 (very likely).');
      return;
    }
    if (!isAuthenticated && (!respondentEmail?.trim() || !respondentName?.trim())) {
      setError('Please enter your name and email.');
      return;
    }
    if (surveyType === 'service_satisfaction' && !appointmentIdFromQuery) {
      setError('Missing appointment. Open the survey link from your dashboard.');
      return;
    }

    setSubmitting(true);
    try {
      const headers = {};
      
      await axios.post(
        `${API_URL}/api/surveys`,
        {
        withCredentials: true,
          surveyType,
          npsScore: Number(npsScore),
          overallRating: overallRating ? Number(overallRating) : null,
          processRating: processRating ? Number(processRating) : null,
          openFeedback: openFeedback.trim() || null,
          followUpCategory: followUpCategory || null,
          appointmentId: surveyType === 'service_satisfaction' ? appointmentIdFromQuery : null,
          carId: carIdFromQuery || null,
          respondentEmail: isAuthenticated ? undefined : respondentEmail.trim(),
          respondentName: isAuthenticated ? undefined : respondentName.trim(),
          source: 'survey_page',
        },
        { headers }
      );
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitIdeas = async (e) => {
    e.preventDefault();
    setError('');
    if (ideaTotalLength < IDEA_MIN_CHARS) {
      setError(`Please write at least ${IDEA_MIN_CHARS} characters across the fields (one short paragraph is enough).`);
      return;
    }
    if (!isAuthenticated && (!respondentEmail?.trim() || !respondentName?.trim())) {
      setError('Please enter your name and email.');
      return;
    }

    setSubmitting(true);
    try {
      const headers = {};
      
      await axios.post(
        `${API_URL}/api/surveys`,
        {
        withCredentials: true,
          surveyType: 'product_ideas',
          answers: {
            featureIdeas: ideaFields.featureIdeas.trim() || undefined,
            uiImprovements: ideaFields.uiImprovements.trim() || undefined,
            integrationsPlugins: ideaFields.integrationsPlugins.trim() || undefined,
            anythingElse: ideaFields.anythingElse.trim() || undefined,
          },
          respondentEmail: isAuthenticated ? undefined : respondentEmail.trim(),
          respondentName: isAuthenticated ? undefined : respondentName.trim(),
          source: 'survey_page',
        },
        { headers }
      );
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const isIdeas = surveyType === 'product_ideas';
    return (
      <div className="survey-page">
        <Navbar />
        <main className="survey-main survey-card survey-success">
          <CheckCircle size={48} className="survey-success-icon" aria-hidden />
          <h1>Thank you</h1>
          <p>
            {isIdeas
              ? 'We read every suggestion and use them to plan features, UI updates, and integrations.'
              : 'Your feedback helps us improve every part of the experience.'}
          </p>
          <div className="survey-success-actions">
            <Link to="/dashboard" className="survey-btn survey-btn--primary">Back to dashboard</Link>
            <Link to="/inventory" className="survey-btn survey-btn--ghost">Browse inventory</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (surveyType === 'product_ideas') {
    return (
      <div className="survey-page">
        <Navbar />
        <main className="survey-main">
          <div className="survey-card survey-card--ideas">
            <div className="survey-card-head">
              <Lightbulb size={28} aria-hidden />
              <h1>{title}</h1>
            </div>
            <p className="survey-lead">
              Tell us what to build or polish next—new tools on the site, clearer flows, integrations, or anything
              that would make your experience better. No purchase required; this is for roadmap ideas only.
            </p>

            <form onSubmit={submitIdeas} className="survey-form">
              <label className="survey-label">
                Features or capabilities you wish we had
                <textarea
                  className="survey-textarea"
                  rows={3}
                  maxLength={4000}
                  value={ideaFields.featureIdeas}
                  onChange={(e) => setIdeaFields((f) => ({ ...f, featureIdeas: e.target.value }))}
                  placeholder="Example: saved searches, trade-in estimator, compare up to 4 cars, dark mode…"
                />
              </label>
              <label className="survey-label">
                UI, layout, or usability improvements
                <textarea
                  className="survey-textarea"
                  rows={3}
                  maxLength={4000}
                  value={ideaFields.uiImprovements}
                  onChange={(e) => setIdeaFields((f) => ({ ...f, uiImprovements: e.target.value }))}
                  placeholder="Example: filters on mobile, larger photos, simpler checkout steps…"
                />
              </label>
              <label className="survey-label">
                Integrations, plugins, or external tools
                <textarea
                  className="survey-textarea"
                  rows={3}
                  maxLength={4000}
                  value={ideaFields.integrationsPlugins}
                  onChange={(e) => setIdeaFields((f) => ({ ...f, integrationsPlugins: e.target.value }))}
                  placeholder="Example: calendar sync, CRM, financing partner, Carfax-style reports…"
                />
              </label>
              <label className="survey-label">
                Anything else?
                <textarea
                  className="survey-textarea"
                  rows={3}
                  maxLength={4000}
                  value={ideaFields.anythingElse}
                  onChange={(e) => setIdeaFields((f) => ({ ...f, anythingElse: e.target.value }))}
                  placeholder="Priorities, pain points, or wild ideas—we want the signal."
                />
              </label>

              {!isAuthenticated && (
                <>
                  <label className="survey-label">
                    Your name
                    <input
                      className="survey-input"
                      value={respondentName}
                      onChange={(e) => setRespondentName(e.target.value)}
                      required
                    />
                  </label>
                  <label className="survey-label">
                    Email
                    <input
                      type="email"
                      className="survey-input"
                      value={respondentEmail}
                      onChange={(e) => setRespondentEmail(e.target.value)}
                      required
                    />
                  </label>
                </>
              )}

              <p className="survey-hint survey-hint--inline">
                {ideaTotalLength} / {IDEA_MIN_CHARS}+ characters (combined)
              </p>

              {error && <p className="survey-error" role="alert">{error}</p>}

              <div className="survey-actions">
                <button type="submit" className="survey-btn survey-btn--primary" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send ideas'}
                </button>
                <button type="button" className="survey-btn survey-btn--ghost" onClick={() => navigate(-1)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="survey-page">
      <Navbar />
      <main className="survey-main">
        <div className="survey-card">
          <div className="survey-card-head">
            <ClipboardList size={28} aria-hidden />
            <h1>{title}</h1>
          </div>

          {surveyType === 'post_purchase' && (
            <p className="survey-lead">
              Tell us how the purchase process went. Your answers are read by our team.
            </p>
          )}
          {surveyType === 'service_satisfaction' && (
            <p className="survey-lead">
              Rate your recent appointment. Only completed visits can be surveyed.
            </p>
          )}
          {surveyType === 'nps_pulse' && (
            <p className="survey-lead">
              One question: how likely are you to recommend us to a friend?
            </p>
          )}

          <form onSubmit={submitClassic} className="survey-form">
            <fieldset className="survey-fieldset">
              <legend>Net Promoter Score (0–10)</legend>
              <p className="survey-hint">0 = not at all likely · 10 = extremely likely</p>
              <div className="survey-nps-row">
                {Array.from({ length: 11 }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`survey-nps-btn${npsScore === i ? ' is-selected' : ''}`}
                    onClick={() => setNpsScore(i)}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </fieldset>

            {(surveyType === 'post_purchase' || surveyType === 'service_satisfaction') && (
              <>
                <fieldset className="survey-fieldset">
                  <legend>Overall satisfaction (1–5 stars)</legend>
                  <p className="survey-hint">1 = very dissatisfied · 5 = very satisfied</p>
                  <div className="survey-nps-row survey-nps-row--five" role="group" aria-label="Overall satisfaction from 1 to 5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`survey-nps-btn${overallRating === n ? ' is-selected' : ''}`}
                        onClick={() => setOverallRating(n)}
                        aria-pressed={overallRating === n}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </fieldset>
                {surveyType === 'post_purchase' && (
                  <fieldset className="survey-fieldset">
                    <legend>Purchase process clarity (1–5)</legend>
                    <p className="survey-hint">1 = unclear · 5 = very clear</p>
                    <div className="survey-nps-row survey-nps-row--five" role="group" aria-label="Purchase process clarity from 1 to 5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={`survey-nps-btn${processRating === n ? ' is-selected' : ''}`}
                          onClick={() => setProcessRating(n)}
                          aria-pressed={processRating === n}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                )}
              </>
            )}

            <label className="survey-label">
              {FOLLOW_UP[0].label}
              <select
                className="survey-input"
                value={followUpCategory}
                onChange={(e) => setFollowUpCategory(e.target.value)}
              >
                {FOLLOW_UP.map((o) => (
                  <option key={o.value || 'none'} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            <label className="survey-label">
              Comments (optional)
              <textarea
                className="survey-textarea"
                rows={4}
                maxLength={4000}
                value={openFeedback}
                onChange={(e) => setOpenFeedback(e.target.value)}
                placeholder="Anything we should know?"
              />
            </label>

            {!isAuthenticated && (
              <>
                <label className="survey-label">
                  Your name
                  <input
                    className="survey-input"
                    value={respondentName}
                    onChange={(e) => setRespondentName(e.target.value)}
                    required
                  />
                </label>
                <label className="survey-label">
                  Email
                  <input
                    type="email"
                    className="survey-input"
                    value={respondentEmail}
                    onChange={(e) => setRespondentEmail(e.target.value)}
                    required
                  />
                </label>
              </>
            )}

            {error && <p className="survey-error" role="alert">{error}</p>}

            <div className="survey-actions">
              <button type="submit" className="survey-btn survey-btn--primary" disabled={submitting}>
                {submitting ? 'Sending…' : 'Submit feedback'}
              </button>
              <button type="button" className="survey-btn survey-btn--ghost" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default SatisfactionSurveyPage;
